import { Employee, Role } from '../types/models';
import { isModuleEnabledForPath } from './moduleAccess';

/** Prefixes only ADMIN may access (non-admin users are denied). Longest match checked first. */
export const DEFAULT_ADMIN_ONLY_PREFIXES: string[] = [
  '/dashboard',
  '/purchase-stocks-list',
  '/source-documents',
  '/employees',
  '/deliveries',
  '/bundles',
  '/sellers',
  '/samples',
  '/routes',
  '/admin',
  '/settings',
  '/audit',
].sort((a, b) => b.length - a.length);

export type RouteAccessRule = { prefix: string; allowed: Role[] };

/**
 * Longest-prefix-first rules. More specific paths must appear before their parents
 * (e.g. `/pos/stock-adjustments` before `/pos`).
 */
export const DEFAULT_ALLOW_RULES: RouteAccessRule[] = [
  { prefix: '/pos/stock-adjustments', allowed: [Role.ADMIN, Role.OFFICE, Role.CASHIER, Role.STORE_MANAGER] },
  {
    prefix: '/thermal-printer',
    allowed: [
      Role.ADMIN,
      Role.OFFICE,
      Role.CASHIER,
      Role.STORE_MANAGER,
      Role.Z_REP,
      Role.I_REP,
      Role.RECOVERY_OFFICER,
      Role.PROCUREMENT,
      Role.WAREHOUSE,
    ],
  },
  { prefix: '/pos', allowed: [Role.ADMIN, Role.OFFICE, Role.CASHIER] },
  { prefix: '/brands', allowed: [Role.ADMIN, Role.OFFICE] },
  {
    prefix: '/customers',
    allowed: [Role.ADMIN, Role.OFFICE, Role.Z_REP, Role.I_REP, Role.RECOVERY_OFFICER],
  },
  { prefix: '/delivery-packages', allowed: [Role.ADMIN, Role.OFFICE, Role.Z_REP] },
  { prefix: '/enquiries', allowed: [Role.ADMIN, Role.OFFICE] },
  { prefix: '/service-tickets', allowed: [Role.ADMIN, Role.OFFICE] },
  { prefix: '/items', allowed: [Role.ADMIN, Role.OFFICE, Role.PROCUREMENT] },
  /** Core stock record (stockId) — admin/office only. */
  { prefix: '/stock-items', allowed: [Role.ADMIN] },
  {
    prefix: '/payments',
    allowed: [
      Role.ADMIN,
      Role.OFFICE,
      Role.Z_REP,
      Role.I_REP,
      Role.RECOVERY_OFFICER,
      Role.CASHIER,
    ],
  },
  {
    prefix: '/grn',
    allowed: [
      Role.ADMIN,
      Role.OFFICE,
      Role.Z_REP,
      Role.I_REP,
      Role.RECOVERY_OFFICER,
      Role.PROCUREMENT,
      Role.STORE_MANAGER,
      Role.CASHIER,
      Role.DRIVER,
      Role.WAREHOUSE,
      Role.ACCOUNTANT,
    ],
  },
  {
    prefix: '/return-to-supplier',
    allowed: [
      Role.ADMIN,
      Role.OFFICE,
      Role.Z_REP,
      Role.I_REP,
      Role.RECOVERY_OFFICER,
      Role.PROCUREMENT,
      Role.STORE_MANAGER,
      Role.CASHIER,
      Role.DRIVER,
      Role.WAREHOUSE,
      Role.ACCOUNTANT,
    ],
  },
  {
    prefix: '/replacement-note',
    allowed: [
      Role.ADMIN,
      Role.OFFICE,
      Role.Z_REP,
      Role.I_REP,
      Role.RECOVERY_OFFICER,
      Role.PROCUREMENT,
      Role.STORE_MANAGER,
      Role.CASHIER,
      Role.DRIVER,
      Role.WAREHOUSE,
      Role.ACCOUNTANT,
    ],
  },
  {
    prefix: '/service-receipt',
    allowed: [
      Role.ADMIN,
      Role.OFFICE,
      Role.Z_REP,
      Role.I_REP,
      Role.RECOVERY_OFFICER,
      Role.PROCUREMENT,
      Role.STORE_MANAGER,
      Role.CASHIER,
      Role.DRIVER,
      Role.WAREHOUSE,
      Role.ACCOUNTANT,
    ],
  },
  { prefix: '/remuneration', allowed: [Role.ADMIN, Role.ACCOUNTANT] },
  { prefix: '/expenses', allowed: [Role.ADMIN, Role.ACCOUNTANT] },
  {
    prefix: '/reports/pdr',
    allowed: [Role.ADMIN, Role.OFFICE, Role.I_REP, Role.RECOVERY_OFFICER],
  },
  {
    prefix: '/reports/irr',
    allowed: [Role.ADMIN, Role.OFFICE, Role.I_REP, Role.RECOVERY_OFFICER],
  },
  {
    prefix: '/reports/recovery-dashboard',
    allowed: [Role.ADMIN, Role.OFFICE, Role.ACCOUNTANT, Role.RECOVERY_OFFICER, Role.I_REP],
  },
  {
    prefix: '/reports/str',
    allowed: [Role.ADMIN, Role.OFFICE, Role.I_REP, Role.STORE_MANAGER, Role.WAREHOUSE],
  },
  { prefix: '/purchase-reports', allowed: [Role.ADMIN, Role.OFFICE, Role.PROCUREMENT] },
  /** Purchase stock hub: admin sees stock report UI; procurement sees purchase reports list (see PurchaseStockPage). */
  { prefix: '/purchase-stock', allowed: [Role.ADMIN, Role.PROCUREMENT] },
  { prefix: '/reports/zdr', allowed: [Role.ADMIN, Role.OFFICE, Role.Z_REP] },
  { prefix: '/zeettu', allowed: [Role.ADMIN, Role.OFFICE, Role.Z_REP] },
  {
    prefix: '/installment-cards',
    allowed: [Role.ADMIN, Role.OFFICE, Role.I_REP, Role.RECOVERY_OFFICER],
  },
  {
    prefix: '/trip',
    allowed: [Role.ADMIN, Role.Z_REP, Role.I_REP, Role.RECOVERY_OFFICER, Role.DRIVER],
  },
  { prefix: '/vehicles', allowed: [Role.ADMIN, Role.DRIVER] },
  { prefix: '/stores', allowed: [Role.ADMIN, Role.STORE_MANAGER, Role.WAREHOUSE] },
  /** Store receiving / inventory (features/stock/store-stock). */
  { prefix: '/store-stock', allowed: [Role.ADMIN, Role.STORE_MANAGER] },
  /** Field rep trip/dispatch stock UX (legacy REP); admins use the full report view. */
  { prefix: '/trip-stock', allowed: [Role.ADMIN, Role.Z_REP, Role.I_REP] },
].sort((a, b) => b.prefix.length - a.prefix.length);

/** First accessible path wins; used after login and for `/` when not admin. */
const POST_LOGIN_REDIRECT_CANDIDATES: string[] = [
  '/grn',
  '/return-to-supplier',
  '/replacement-note',
  '/service-receipt',
  '/remuneration',
  '/expenses',
  '/stores',
  '/store-stock',
  '/pos/sales',
  '/pos/stock-adjustments',
  '/vehicles',
  '/trip',
  '/customers',
  '/items',
  '/purchase-reports',
  '/payments',
  '/brands',
  '/enquiries',
  '/service-tickets',
  '/delivery-packages',
  '/reports/zdr',
  '/reports/pdr',
  '/reports/irr',
  '/reports/recovery-dashboard',
  '/reports/str',
  '/zeettu',
  '/installment-cards',
  '/trip-stock',
  '/purchase-stock',
];

let runtimeAllowRules: RouteAccessRule[] | null = null;
let runtimeAdminOnlyPrefixes: string[] | null = null;

export function setRouteAccessConfig(
  rules: RouteAccessRule[] | null,
  adminOnlyPrefixes: string[] | null,
): void {
  runtimeAllowRules = rules;
  runtimeAdminOnlyPrefixes = adminOnlyPrefixes;
}

export function getRouteAccessConfig(): {
  allowRules: RouteAccessRule[];
  adminOnlyPrefixes: string[];
} {
  return {
    allowRules: runtimeAllowRules ?? DEFAULT_ALLOW_RULES,
    adminOnlyPrefixes: runtimeAdminOnlyPrefixes ?? DEFAULT_ADMIN_ONLY_PREFIXES,
  };
}

export function getEffectiveRoles(user: Employee | null): Role[] {
  if (!user) return [];
  if (user.roles && user.roles.length > 0) {
    return [...new Set(user.roles)];
  }
  return user.role ? [user.role] : [];
}

export function isSuperAdmin(roles: Role[]): boolean {
  return roles.includes(Role.SUPER_ADMIN);
}

/** Admin-equivalent route/data bypass: admin or super_admin. */
export function isAdmin(roles: Role[]): boolean {
  return roles.includes(Role.ADMIN) || roles.includes(Role.SUPER_ADMIN);
}

function pathMatchesPrefix(pathname: string, prefix: string): boolean {
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

function isAdminOnlyPath(pathname: string): boolean {
  const { adminOnlyPrefixes } = getRouteAccessConfig();
  return adminOnlyPrefixes.some((p) => pathMatchesPrefix(pathname, p));
}

/**
 * Whether the user should see the simplified rep trip/dispatch stock view (not the full report).
 */
export function shouldShowRepFieldStockView(roles: Role[]): boolean {
  if (isAdmin(roles)) return false;
  return roles.includes(Role.Z_REP) || roles.includes(Role.I_REP);
}

/**
 * Route access for authenticated users. Does not handle /login or /register.
 */
export function canAccessPath(pathname: string, roles: Role[]): boolean {
  const path = pathname.split('?')[0] || '/';

  if (!isModuleEnabledForPath(path)) {
    return false;
  }

  if (isAdmin(roles)) {
    return true;
  }

  if (isAdminOnlyPath(path)) {
    return false;
  }

  const { allowRules } = getRouteAccessConfig();
  for (const rule of allowRules) {
    if (pathMatchesPrefix(path, rule.prefix)) {
      return rule.allowed.some((r) => roles.includes(r));
    }
  }

  return false;
}

export function getPostLoginRedirectPath(roles: Role[]): string {
  if (isAdmin(roles)) {
    return '/dashboard';
  }
  for (const path of POST_LOGIN_REDIRECT_CANDIDATES) {
    if (isModuleEnabledForPath(path) && canAccessPath(path, roles)) {
      return path;
    }
  }
  return '/customers';
}
